import { useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { PiCamera } from 'react-icons/pi'
import to from 'await-to-js'
import { useMountEffect } from 'hooks/useMountEffect'
import { Button } from '@firstbite/ui'

type Mode = 'idle' | 'camera' | 'preview'

/**
 * Wizard capture step: take a photo (getUserMedia) or upload an image, then
 * report the chosen File up via `onFileChange`. Extraction + advancing happens
 * on the wizard's footer "Next" (enabled once a file is selected). The body owns
 * only the camera/upload/preview controls.
 */
export default function SnapStepBody({
  onFileChange,
}: {
  onFileChange: (file?: File) => void
}) {
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [mode, setMode] = useState<Mode>('idle')
  const [previewUrl, setPreviewUrl] = useState<string | undefined>()
  // Mirrors previewUrl so the unmount cleanup can revoke the current blob URL —
  // the ref survives into the cleanup closure, the state value doesn't. Always
  // change the preview through updatePreviewUrl, never setPreviewUrl directly.
  const previewUrlRef = useRef<string | undefined>(undefined)

  const updatePreviewUrl = (url: string | undefined) => {
    // Revoke the previous blob URL before replacing or clearing it.
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    setPreviewUrl(url)
    previewUrlRef.current = url
  }

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }

  useMountEffect(() => () => {
    stopStream()
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
  })

  const selectFile = (file: File) => {
    updatePreviewUrl(URL.createObjectURL(file))
    setMode('preview')
    onFileChange(file)
  }

  const startCamera = async () => {
    const getUserMedia = navigator.mediaDevices?.getUserMedia
    if (!getUserMedia) {
      cameraInputRef.current?.click()
      return
    }
    const [err, stream] = await to(
      navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      })
    )
    if (err || !stream) {
      toast.error(
        'Could not access the camera. You can upload an image instead.'
      )
      return
    }
    streamRef.current = stream
    setMode('camera')
  }

  const attachVideo = (node: HTMLVideoElement | null) => {
    videoRef.current = node
    if (node && streamRef.current) {
      node.srcObject = streamRef.current
      void node.play()
    }
  }

  const capturePhoto = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob(
      (blob) => {
        if (!blob) return
        stopStream()
        selectFile(new File([blob], 'card.jpg', { type: 'image/jpeg' }))
      },
      'image/jpeg',
      0.92
    )
  }

  const cancelCamera = () => {
    stopStream()
    setMode('idle')
  }

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (!selected) return
    selectFile(selected)
  }

  const reset = () => {
    updatePreviewUrl(undefined)
    setMode('idle')
    onFileChange(undefined)
  }

  return (
    <div className="flex flex-col items-center justify-center p-6 gap-4">
      <input
        ref={cameraInputRef}
        data-testid="card-camera-input"
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleSelect}
      />
      <input
        ref={uploadInputRef}
        data-testid="card-upload-input"
        type="file"
        accept="image/*,.heic,.heif"
        className="hidden"
        onChange={handleSelect}
      />

      {mode === 'camera' && (
        <>
          <video
            ref={attachVideo}
            autoPlay
            playsInline
            muted
            className="max-h-80 w-full rounded-xl border border-line bg-black object-contain"
          />
          <div className="flex w-full gap-4">
            <Button
              className="flex-1"
              type="button"
              size="lg"
              variant="primary"
              appearance="outline"
              onClick={cancelCamera}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              type="button"
              size="lg"
              variant="primary"
              onClick={capturePhoto}
            >
              Capture
            </Button>
          </div>
        </>
      )}

      {mode === 'preview' && previewUrl && (
        <>
          <img
            src={previewUrl}
            alt="Captured card"
            className="max-h-80 rounded-xl border border-line"
          />
          <Button
            className="w-full"
            type="button"
            size="lg"
            variant="primary"
            appearance="outline"
            onClick={reset}
          >
            Replace
          </Button>
        </>
      )}

      {mode === 'idle' && (
        <div className="flex w-full flex-col items-center gap-4">
          <Button
            className="w-full"
            type="button"
            size="lg"
            variant="primary"
            onClick={startCamera}
          >
            <PiCamera />
            Take a photo
          </Button>
          <button
            type="button"
            onClick={() => uploadInputRef.current?.click()}
            className="text-sm font-medium text-brand-active"
          >
            Upload a photo instead
          </button>
        </div>
      )}
    </div>
  )
}
